import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import styles from "./TableCells.module.scss";

export type SimpleTableFooterCellProps = ComponentPropsWithoutRef<"td"> & {
  colSpan?: number;
};

export type DataTableFooterCellProps = ComponentPropsWithoutRef<"div"> & {
  colSpan?: number;
};

/**
 * The props for the TableFooterCell component
 */
export type TableFooterCellProps =
  | ({
      /**
       * The type of table being rendered
       */
      type: "simple";
    } & SimpleTableFooterCellProps)
  | ({
      /**
       * The type of table being rendered
       */
      type: "data-table";
    } & DataTableFooterCellProps);

/**
 * The styled table footer cell component
 * @param props - The props for the TableFooterCell component
 * @param props.type - The type of table being rendered
 * @param props.children - The children to render in the cell
 * @param props.className - The class name of the TableFooterCell component
 * @param props.rest - The rest of the props
 */
export const TableFooterCell = forwardRef<
  HTMLTableCellElement | HTMLDivElement,
  TableFooterCellProps
>((props, ref) => {
  const { type, children, className, ...rest } = props;

  const classes = cx(styles["table-footer-cell"], className);
  const CellElement = type === "simple" ? "td" : "div";

  return (
    <CellElement
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- could be table cell or div
      ref={ref as any}
      data-anv="table-footer-cell"
      {...rest}
      className={classes}
      role={type === "simple" ? undefined : "gridcell"}
    >
      {children}
    </CellElement>
  );
});

TableFooterCell.displayName = "TableFooterCell";
